
import Vue from 'vue';
import {
  EntityType, ExtractedEntity, IngestFragment, IngestDocument, FileDescriptor, Label, Suggestion,
} from '@/model';
import { traverse } from '@/util/functional-iterator';
import DocumentFragment from '@/components/DocumentFragment.vue';
import { groupBy } from '@/util/array-util';
import ExpandableFragment from '@/components/ExpandableFragment.vue';
import labelStore, { labelTypeSorter } from '@/store/labels';

type RenderedFragment = {
  fragment: IngestFragment;
  top: number;
  hover: (value: boolean) => void;
}

const fragmentHeight = 18;

export default Vue.extend({
  components: { DocumentFragment, ExpandableFragment },
  name: 'DocumentFragmentScroller',

  props: {
    fragments: { type: Array as () => RenderedFragment[], default: () => [] },
    zoom: { type: Number, default: 1 },
    context: { type: Array as () => Label[], default: () => [] },
    documentId: { type: Number },
    selectedText: { type: String, default: '' },
    suggestions: { type: Array as () => Suggestion[], default: () => [] },
    priorityTypes: { type: Array as () => string[], default: () => [] },
    ingest: { type: Object as () => IngestDocument | undefined, required: false },
    media: { type: Array as () => FileDescriptor[] | undefined, required: false },
    extractedEntities: { type: Array as () => ExtractedEntity[], default: () => [] },
    entityTypes: { type: Array as () => EntityType[], default: () => [] },
  },

  data() {
    return {
      editing: false,
      offset: {
        left: 0,
        top: 0,
      },
    };
  },

  mounted() {
    const element = this.$el as HTMLElement;
    [this.offset.left, this.offset.top] = traverse(element,
      (el) => el?.offsetParent as HTMLElement || el.parentElement?.offsetParent || undefined)
      .prepend([element])
      .collect()
      .reduce((acc, p) => [acc[0] + p.offsetLeft, acc[1] + p.offsetTop], [0, 0]);
  },

  computed: {
    segmentedFragments(): ReadonlyMap<number, readonly RenderedFragment[]> {
      const groups = groupBy(this.fragments, this.fragmentTop);
      const sorter = labelTypeSorter((f: RenderedFragment) => this.primaryLabelType(f.fragment));
      return new Map(Array.from(groups, ([top, frags]) => [top, [...frags].sort(sorter)]));
    },
    anyComments(): boolean {
      return this.fragments.some((f) => this.showingCommentIcon(f.fragment));
    },
  },

  methods: {
    primaryLabelType(fragment: IngestFragment): string {
      return labelStore.getters.lookup.get(fragment.labels[0]?.labelId)?.type ?? '';
    },
    fragmentTop(fragment: RenderedFragment) {
      return ((Math.floor(fragment.top / fragmentHeight) * fragmentHeight)) - this.offset.top;
    },
    onSaveFragment(fragment: IngestFragment) {
      this.$emit('saveFragment', fragment);
    },
    onDeleteFragment(id: number) {
      // eslint-disable-next-line no-alert
      if (id === 0 || window.confirm('Are you sure you want to delete this fragment?')) {
        this.$emit('deleteFragment', id);
      }
    },
    onRevertFragment(id: number) {
      this.$emit('revertFragment', id);
    },
    edit(value: boolean, fragment: IngestFragment) {
      this.editing = value;
      this.$emit('edit', value ? fragment : undefined);
    },
    showingCommentIcon(fragment: IngestFragment): boolean {
      return fragment.description !== undefined && fragment.description.trim().length > 0;
    },
  },

});
